import { AxiosError, type AxiosResponse, isAxiosError } from "axios";
import {
	type FormikConfig,
	type FormikErrors,
	type FormikTouched,
	type FormikValues,
	useFormik,
} from "formik";
import type { ValidationExceptionResponse } from "shared";
import { useEventCallback } from "usehooks-ts";
import type { ZodError } from "zod";
import { AlertType, useAlertEmit } from "../alert/Alert";

const axiosNetworkErrorMessages: Record<string, string> = {
	[AxiosError.ERR_NETWORK]: "There is an issue with your network connectivity",
	[AxiosError.ETIMEDOUT]:
		"Your action timed out. This might be due to a slow connection or an issue on our end.",
};

type UseFormikReturn<T extends FormikValues> = ReturnType<typeof useFormik<T>>;

type GetFieldState<T> = (name: keyof T) => {
	error: string | undefined;
	touched: boolean | undefined;
};

export type UseFormReturn<T extends FormikValues> = UseFormikReturn<T> & {
	getFieldState: GetFieldState<T>;
};

export function useForm<T extends FormikValues>(
	props: FormikConfig<T>,
): UseFormReturn<T> {
	const emit = useAlertEmit();

	const popError = useEventCallback((content: string) =>
		emit({
			type: AlertType.ERROR,
			content,
		}),
	);

	const onSubmit = useEventCallback(props.onSubmit);

	const formik = useFormik<T>({
		...props,
		// In order to keep server validations we need both booleans underneath to be false
		validateOnChange: false,
		validateOnBlur: false,
		onSubmit: async (values, helpers) => {
			try {
				await onSubmit(values, helpers);
			} catch (e) {
				if (isAxiosError(e)) {
					if (e.response === undefined) {
						// Network error
						if (e.code === AxiosError.ERR_CANCELED) {
							// Cancelled by consumer
							return;
						}

						// Handle recognised error codes
						if (e.code && Object.hasOwn(axiosNetworkErrorMessages, e.code)) {
							popError(axiosNetworkErrorMessages[e.code]);
							return;
						}

						popError("There was an unknown network issue");
						return;
					}

					if (e.response.status === 422) {
						const errors = parseValidationErrors(e.response);
						await helpers.setTouched(touchedFromErrors(errors));
						helpers.setErrors(errors);
						return;
					}

					popError(e.response.data?.message || "Something went wrong.");
					return;
				}

				// No error message matched - rethrow error
				throw e;
			}
		},
	}) satisfies UseFormikReturn<T>;

	return {
		...formik,
		getFieldState: (name: keyof T) => {
			const errors = formik.errors[name];
			return {
				error: Array.isArray(errors) ? errors.join(". ") : (errors as string),
				touched: formik.touched[name] as boolean | undefined,
			};
		},
	};
}

export function zodErrorToFormikErrors<T>(
	issues: ZodError<T>["issues"],
): FormikErrors<T> {
	const formikErrors: Record<string, string> = {};

	for (const issue of issues) {
		const path = issue.path.join(".");

		if (formikErrors[path]) {
			formikErrors[path] += ` ${issue.message}`;
		} else {
			formikErrors[path] = issue.message;
		}
	}

	return formikErrors as FormikErrors<T>;
}

const parseValidationErrors = <T,>(
	res: AxiosResponse<ValidationExceptionResponse>,
): FormikErrors<T> => {
	const errors = res.data.errors;
	if (Array.isArray(errors)) {
		return zodErrorToFormikErrors(errors);
	}

	// Legacy - to be remove once we migrate to Zod completely
	return Object.entries(res.data.errors).reduce(
		(previousValue, [key, value]) => ({
			// biome-ignore lint/performance/noAccumulatingSpread: Baseline
			...previousValue,
			[key]: value.join(" "),
		}),
		{},
	);
};

function touchedFromErrors<T>(errors: FormikErrors<T>) {
	return Object.keys(errors).reduce(
		(prev, key) => ({
			// biome-ignore lint/performance/noAccumulatingSpread: Baseline
			...prev,
			[key]: true,
		}),
		{},
	) as FormikTouched<T>;
}

// biome-ignore lint/suspicious/noExplicitAny: Generic form helper needs any for flexibility
export function toFormValues<T extends Record<string, any>>(
	obj: T,
): { [key in keyof T]: string } {
	const result: { [key: string]: string } = {};
	for (const [key, value] of Object.entries(obj)) {
		if (value === undefined || value === null) {
			result[key] = "";
		} else {
			result[key] = String(value);
		}
	}
	return result as { [key in keyof T]: string };
}
